import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Building2, Users, Mail, TrendingUp, Home, Plus, ChevronRight, Calendar } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { useAuth } from '../../contexts/AuthContext';
import { clientStatusLabels, interactionTypeLabels } from '../../lib/utils';

export default function AdminDashboard() {
  const { api } = useAuth();
  const [stats, setStats] = useState(null);
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchDashboardData = useCallback(async () => {
    try {
      const [statsRes, activityRes] = await Promise.all([
        api.get('/dashboard/stats'),
        api.get('/dashboard/recent-activity')
      ]);
      setStats(statsRes.data);
      setRecentActivity(activityRes.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-blue"></div>
      </div>
    );
  }

  const statCards = [
    {
      title: 'Total de Imóveis',
      value: stats?.properties?.total || 0,
      subtitle: `${stats?.properties?.available || 0} disponíveis`,
      icon: Building2,
      color: 'bg-blue-500',
      link: '/admin/properties'
    },
    {
      title: 'Imóveis Destaque',
      value: stats?.properties?.featured || 0,
      subtitle: `${stats?.properties?.new_this_month || 0} novos este mês`,
      icon: Home,
      color: 'bg-brand-gold',
      link: '/admin/properties'
    },
    {
      title: 'Clientes',
      value: stats?.clients?.total || 0,
      subtitle: `${stats?.clients?.by_stage?.new || 0} novos`,
      icon: Users,
      color: 'bg-green-500',
      link: '/admin/clients'
    },
    {
      title: 'Leads',
      value: stats?.leads?.total || 0,
      subtitle: `${stats?.leads?.new || 0} não tratados`,
      icon: Mail,
      color: 'bg-purple-500',
      link: '/admin/leads'
    }
  ];

  const pipelineStages = [
    { key: 'new', label: 'Novos', color: 'bg-slate-200' },
    { key: 'contacted', label: 'Contatados', color: 'bg-blue-200' },
    { key: 'visit_scheduled', label: 'Visita Agendada', color: 'bg-yellow-200' },
    { key: 'proposal', label: 'Proposta', color: 'bg-orange-200' },
    { key: 'closed_won', label: 'Fechados (Ganho)', color: 'bg-green-200' },
    { key: 'closed_lost', label: 'Fechados (Perdido)', color: 'bg-red-200' }
  ];

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-slate-800">Dashboard</h1>
          <p className="text-slate-500 mt-1">Visão geral do seu negócio imobiliário.</p>
        </div>
        <Button asChild className="bg-brand-blue hover:bg-brand-blue-dark">
          <Link to="/admin/properties/new">
            <Plus className="w-4 h-4 mr-2" />
            Novo Imóvel
          </Link>
        </Button>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((card) => (
          <Link key={card.title} to={card.link}>
            <Card className="hover:shadow-md transition-shadow cursor-pointer">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-slate-500 mb-1">{card.title}</p>
                    <p className="text-3xl font-bold text-slate-800">{card.value}</p>
                    <p className="text-sm text-slate-400 mt-1">{card.subtitle}</p>
                  </div>
                  <div className={`${card.color} p-3 rounded-xl`}>
                    <card.icon className="w-6 h-6 text-white" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      {/* Pipeline Overview */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading text-lg">Pipeline de Clientes</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {pipelineStages.map((stage) => (
              <div key={stage.key} className={`${stage.color} rounded-lg p-4 text-center`}>
                <div className="text-2xl font-bold text-slate-800">
                  {stats?.clients?.by_stage?.[stage.key] || 0}
                </div>
                <div className="text-xs text-slate-600 mt-1">{stage.label}</div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Recent Activity & Quick Actions */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Recent Activity */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="font-heading text-lg">Atividade Recente</CardTitle>
            <TrendingUp className="w-5 h-5 text-slate-400" />
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {recentActivity.length > 0 ? (
                recentActivity.slice(0, 8).map((activity, index) => (
                  <div key={index} className="flex items-start gap-3 pb-3 border-b border-slate-100 last:border-0">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                      activity.type === 'property' ? 'bg-blue-100' :
                      activity.type === 'lead' ? 'bg-purple-100' :
                      'bg-green-100'
                    }`}>
                      {activity.type === 'property' && <Building2 className="w-4 h-4 text-blue-600" />}
                      {activity.type === 'lead' && <Mail className="w-4 h-4 text-purple-600" />}
                      {activity.type === 'interaction' && <Users className="w-4 h-4 text-green-600" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-slate-800 truncate">{activity.title}</p>
                      <p className="text-xs text-slate-400">
                        {activity.type === 'property' && 'Imóvel cadastrado'}
                        {activity.type === 'lead' && 'Novo lead recebido'}
                        {activity.type === 'interaction' && interactionTypeLabels[activity.action]}
                      </p>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-slate-400 text-sm text-center py-4">Nenhuma atividade recente.</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card>
          <CardHeader>
            <CardTitle className="font-heading text-lg">Ações Rápidas</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 gap-3">
              <Link 
                to="/admin/properties/new" 
                className="flex items-center justify-between p-4 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-brand-blue rounded-lg flex items-center justify-center">
                    <Plus className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-800">Cadastrar Imóvel</p>
                    <p className="text-xs text-slate-500">Adicione um novo imóvel ao catálogo</p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-slate-400" />
              </Link>
              
              <Link 
                to="/admin/clients/new" 
                className="flex items-center justify-between p-4 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center">
                    <Users className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-800">Novo Cliente</p>
                    <p className="text-xs text-slate-500">Cadastre um novo cliente no CRM</p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-slate-400" />
              </Link>
              
              <Link 
                to="/admin/leads" 
                className="flex items-center justify-between p-4 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple-500 rounded-lg flex items-center justify-center">
                    <Mail className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-800">Ver Leads</p>
                    <p className="text-xs text-slate-500">
                      {stats?.leads?.new || 0} leads aguardando tratamento
                    </p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-slate-400" />
              </Link>

              <Link 
                to="/" 
                target="_blank"
                className="flex items-center justify-between p-4 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-brand-gold rounded-lg flex items-center justify-center">
                    <Home className="w-5 h-5 text-brand-blue" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-800">Ver Site Público</p>
                    <p className="text-xs text-slate-500">Abrir site em nova aba</p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-slate-400" />
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
